import React, { useEffect, useState } from 'react';
import type { AdminApiClient } from './api-client';
import './product-thumbnail.css';

type Props = {
  api: AdminApiClient;
  productId: string;
  src?: string;
  alt: string;
  className?: string;
  scopeKey?: string;
};

export function productImageSource(src: string, productId: string) {
  if (!src.trim()) return { direct: '', managed: '' };
  try {
    const url = new URL(src, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) return { direct: '', managed: '' };
    const match = url.pathname.match(/^\/api\/customer\/public\/m2\/media\/([^/]+)\/content$/);
    if (match) {
      return {
        direct: '',
        managed: `/api/admin/m2/products/${encodeURIComponent(productId)}/media/${encodeURIComponent(decodeURIComponent(match[1]!))}/content`,
      };
    }
    return { direct: url.href, managed: '' };
  } catch {
    return { direct: '', managed: '' };
  }
}

// A new resource/scope must never paint the previous product's image.
export function ProductThumbnail(props: Props) {
  return (
    <ThumbnailImage
      key={`${props.productId}|${props.src ?? ''}|${props.scopeKey ?? ''}`}
      {...props}
    />
  );
}

function ThumbnailImage({ api, productId, src = '', alt, className = '' }: Props) {
  const { direct, managed } = productImageSource(src, productId);
  const [imageUrl, setImageUrl] = useState(direct);
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>(
    !src ? 'empty' : direct || managed ? 'loading' : 'error',
  );
  useEffect(() => {
    if (!direct && !managed) return;
    const controller = new AbortController();
    let active = true;
    let objectUrl = '';
    const timeout = window.setTimeout(() => {
      controller.abort();
      if (active) setState((current) => (current === 'ready' ? current : 'error'));
    }, 10000);
    if (managed) {
      void api
        .download(managed, {}, controller.signal)
        .then((blob) => {
          if (!active || controller.signal.aborted) return;
          if (!/^image\/(jpeg|png|webp|gif|avif)$/i.test(blob.type))
            throw new Error('Invalid image');
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        })
        .catch(() => {
          if (active) setState('error');
        });
    }
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, direct, managed]);

  const label =
    state === 'empty' ? '未设置图片' : state === 'error' ? '图片暂不可用' : '图片加载中';
  return (
    <div className={`product-thumbnail ${className}`} data-image-state={state}>
      {state !== 'ready' && (
        <div className="product-thumbnail-placeholder" role="img" aria-label={label}>
          <svg viewBox="0 0 120 64" aria-hidden="true">
            <circle cx="91" cy="16" r="8" fill="#ecdab0" />
            <path d="M8 55 40 16 68 55Z" fill="#aacdc3" />
            <path d="m43 55 32-30 36 30Z" fill="#81b4a5" />
            <path d="m31 27 9-11 10 13-10-4Z" fill="#f5faf7" />
            <path
              d="M61 58c-25-9 29-11 8-21"
              fill="none"
              stroke="#f5faf7"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
          <span>{label}</span>
        </div>
      )}
      {imageUrl && state !== 'error' && (
        <img
          className="product-thumbnail-image"
          src={imageUrl}
          alt={alt}
          referrerPolicy="no-referrer"
          decoding="async"
          style={{ opacity: state === 'ready' ? 1 : 0 }}
          onLoad={() => setState('ready')}
          onError={() => setState('error')}
        />
      )}
    </div>
  );
}
